import PageController from '@mm/state/controllers/PageController';
import ButtonModel, {
  ButtonType,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, {
  DialogueWidget,
  GalleryWidgetConfig,
  TextResponse,
  UserFormConfig,
  WidgetConfigEvenLessUseful,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { getUniqId } from '@mm/utils/src/get-uniq-id';
import { cloneDeep, differenceWith, findLast } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialoguePreview } from './use-dialogue-preview';
import { usePrevious } from '../../common/hooks/use-previous';

const getUserMessage = (message: string): DialogueWidget => {
  return {
    id: getUniqId('message'),
    type: 'user_message',
    message,
  };
};

const getResponseMessage = (entry: Partial<TextResponse>): TextResponse => {
  return {
    id: entry.id || getUniqId(1),
    type: WidgetTypes.TEXT,
    config: {
      body: entry.config?.body || '',
    },
    buttons: entry.buttons || [],
    validated: {
      facebook: true,
      buttonDialogueInValid: false,
    },
    pageId: undefined,
  };
};

export const isUserMessage = (w: DialogueWidget) =>
  w.type === 'user_message' || (w.type === WidgetTypes.TEXT && !w.pageId);

const getFreeTextResponse = (type: string | null): DialogueWidget[] => {
  switch (type) {
    case 'email':
    case ButtonType.EMAIL:
      return [getUserMessage('example@mail.com')];
    case 'emails':
      return [
        getUserMessage('example@mail.com'),
        getUserMessage('example2@mail.com'),
      ];
    case 'phone':
    case ButtonType.PHONE_NUMBER:
      return [getUserMessage('+123456789')];
    case 'number':
      return [getUserMessage('123')];
    case 'url':
      return [getUserMessage('example.com')];
    default:
      return [getUserMessage('Answer to question')];
  }
};

const getWidgetsWithAnswers = (widgets: DialogueWidget[]) => {
  return widgets.reduce((acc: DialogueWidget[], w) => {
    if (
      w.type !== 'user_message' &&
      [ButtonType.EMAIL, ButtonType.PHONE_NUMBER].includes(w.buttons?.[0]?.type)
    ) {
      return [...acc, w, ...getFreeTextResponse(w.buttons?.[0].type)];
    }
    if (
      w.type !== 'user_message' &&
      !w.buttons.length &&
      (w.config as WidgetConfigEvenLessUseful).allowFreeTextResponse
    ) {
      return [...acc, w, ...getFreeTextResponse('free')];
    }

    return [...acc, w];
  }, []);
};

const getPreviewItemsForWidget = async (
  widget: WidgetModel,
  getDialogueById: (pageId: number) => any,
  setWidgetByPageId: React.Dispatch<
    React.SetStateAction<Map<number, DialogueWidget[]>>
  >,
): Promise<DialogueWidget[]> => {
  const { type, metadata, buttons, config } = widget;
  switch (type) {
    case WidgetTypes.QUICK_QUESTION:
    case WidgetTypes.SMS_OPT_IN_REPLY:
    case WidgetTypes.SMS_OPT_IN_V2:
    case WidgetTypes.SMS_OPT_IN:
    case WidgetTypes.OPT_IN:
    case WidgetTypes.TEXT: {
      const btn =
        buttons.length && metadata?.selectedBtnIdx !== undefined
          ? widget.buttons[metadata.selectedBtnIdx]
          : undefined;

      if (btn) {
        const widgets = await getWidgetsByButtonType(
          btn,
          getDialogueById,
          setWidgetByPageId,
        );
        const widgetsWithAnswers = getWidgetsWithAnswers(widgets);

        return [widget, ...widgetsWithAnswers];
      }
      if (widget.config.allowFreeTextResponse) {
        return [widget, getUserMessage('Answer to question')];
      }
      return [widget];
    }
    case WidgetTypes.OTN: {
      if (widget.metadata?.selectedBtnIdx !== undefined) {
        const btn: ButtonModel = {
          id: widget.id,
          name: 'Notify me',
          type: ButtonType.PAGE,
          sortOrder: 0,
          config: {
            pageId: widget.config.pageId,
          },
        };
        const widgets = await getWidgetsByButtonType(
          btn,
          getDialogueById,
          setWidgetByPageId,
        );
        const widgetsWithAnswers = getWidgetsWithAnswers(widgets);
        return [widget, ...widgetsWithAnswers];
      }
      return [widget];
    }
    case WidgetTypes.NAVIGATE: {
      if (widget.metadata?.selectedBtnIdx !== undefined) {
        const btn: ButtonModel = {
          id: widget.id,
          name: 'NAVIGATE',
          type: ButtonType.PAGE,
          sortOrder: 0,
          config: {
            pageId: widget.config.pageId,
          },
        };
        const widgets = await getWidgetsByButtonType(
          btn,
          getDialogueById,
          setWidgetByPageId,
        );
        const widgetsWithAnswers = getWidgetsWithAnswers(widgets);
        return [
          {
            ...widget,
            metadata: { ...widget.metadata, isPathEmpty: !widgets.length },
          },
          ...widgetsWithAnswers,
        ];
      }
      return [widget];
    }

    case WidgetTypes.GALLERY: {
      const entry =
        config.entries?.length && metadata?.selectedEntryIdx !== undefined
          ? config.entries[metadata.selectedEntryIdx]
          : undefined;

      if (entry) {
        const btn =
          entry.buttons.length && metadata?.selectedBtnIdx !== undefined
            ? entry.buttons[metadata.selectedBtnIdx]
            : undefined;

        if (btn) {
          const widgets = await getWidgetsByButtonType(
            btn,
            getDialogueById,
            setWidgetByPageId,
          );
          const widgetsWithAnswers = getWidgetsWithAnswers(widgets);

          return [widget, ...widgetsWithAnswers];
        }
        return [widget];
      }
      return [widget];
    }

    case WidgetTypes.FORM: {
      if (config.entries) {
        let btnExists = false;
        const widgetsToSave = await (config as UserFormConfig).entries.reduce(
          async (acc, entry, idx) => {
            const elements = await acc;
            if (!btnExists) {
              const firstWidget = getResponseMessage({
                id: entry.id,
                config: {
                  body: entry.body,
                },
                buttons: entry.buttons,
              });

              if (entry.buttons.length) {
                btnExists = true;
                const btnWithDefaultResponse = [
                  ButtonType.EMAIL,
                  ButtonType.PHONE_NUMBER,
                ].includes(entry.buttons?.[0].type);
                const btnIdx = metadata?.selectedBtnForEntry?.[idx];
                const btn = btnWithDefaultResponse
                  ? entry.buttons[0]
                  : btnIdx !== undefined
                  ? entry.buttons[btnIdx]
                  : undefined;

                if (btn) {
                  btnExists = false;
                  const widgets = await getWidgetsByButtonType(
                    btn,
                    getDialogueById,
                    setWidgetByPageId,
                  );
                  const widgetsWithAnswers = getWidgetsWithAnswers(widgets);

                  if (entry.response) {
                    return [
                      ...elements,
                      firstWidget,
                      ...widgetsWithAnswers,
                      getResponseMessage({ config: { body: entry.response } }),
                    ];
                  }
                  return [...elements, firstWidget, ...widgetsWithAnswers];
                }
                return [...elements, firstWidget];
              }
              if (entry.allowFreeTextResponse) {
                if (entry.response) {
                  return [
                    ...elements,
                    firstWidget,
                    ...getFreeTextResponse(entry.validationRule),
                    getResponseMessage({ config: { body: entry.response } }),
                  ];
                }
                return [
                  ...elements,
                  firstWidget,
                  ...getFreeTextResponse(entry.validationRule),
                ];
              }
              return [...elements, firstWidget];
            }
            return elements;
          },
          Promise.resolve([] as DialogueWidget[]),
        );

        return [
          {
            ...widget,
            metadata: { ...widget.metadata, formWidgets: widgetsToSave },
          },
        ];
      }
      return [widget];
    }

    default:
      return [widget];
  }
};

const getWidgetsByButtonType = async (
  btn: ButtonModel,
  getDialogueById: (pageId: number) => any,
  setWidgetByPageId: React.Dispatch<
    React.SetStateAction<Map<number, DialogueWidget[]>>
  >,
): Promise<DialogueWidget[]> => {
  const { type } = btn;
  switch (type) {
    case ButtonType.PAGE:
    case ButtonType.OPT_IN: {
      const dialogue = (btn.config as PageButtonConfig).pageId
        ? await getDialogueById((btn.config as PageButtonConfig).pageId)
        : { widgets: [] };
      setWidgetByPageId(widgetByPageId =>
        new Map(widgetByPageId).set(
          (btn.config as PageButtonConfig).pageId,
          dialogue.widgets,
        ),
      );
      const userMessage =
        btn.name !== 'NAVIGATE' ? [getUserMessage(btn?.name ?? 'btn')] : [];
      return btn ? [...userMessage, ...dialogue.widgets] : dialogue.widgets;
    }
    case ButtonType.EMAIL: {
      return [getUserMessage('example@mail.com')];
    }
    case ButtonType.PHONE_NUMBER: {
      return [getUserMessage('+123456789')];
    }
    case ButtonType.POSTBACK: {
      return [getUserMessage(btn.name ?? 'user')];
    }
    default: {
      return [];
    }
  }
};

const getWidgetsWithFormAndAnswers = async (
  preview: DialogueWidget[],
  getDialogueById: (pageId: number) => any,
  setWidgetByPageId: React.Dispatch<
    React.SetStateAction<Map<number, DialogueWidget[]>>
  >,
  includeAnswers: boolean,
  platforms?: PlatformType[],
) => {
  let nextPreview = await preview.reduce(async (items, w) => {
    const elements = await items;
    if (w.type === WidgetTypes.FORM) {
      const el = await getPreviewItemsForWidget(
        w,
        getDialogueById,
        setWidgetByPageId,
      );
      return [...elements, ...el];
    }
    if (w.type === WidgetTypes.NAVIGATE) {
      const btn: ButtonModel = {
        id: w.id,
        name: 'NAVIGATE',
        type: ButtonType.PAGE,
        sortOrder: 0,
        config: {
          pageId: w.config.pageId,
        },
      };
      return [...elements, { ...w, buttons: [btn] }];
    }
    return [...elements, w];
  }, Promise.resolve([] as WidgetModel[]));
  if (platforms?.length) {
    nextPreview = nextPreview.filter(
      widget =>
        isUserMessage(widget) ||
        platforms.every(p => (widget as WidgetModel).supportedByPlatforms[p]),
    );
  }
  if (includeAnswers) {
    const nextPreviewAnswers = getWidgetsWithAnswers(nextPreview);
    return nextPreviewAnswers.filter(
      widget =>
        widget.type === 'user_message' ||
        (widget.validated.facebook && !widget.validated.buttonDialogueInValid),
    );
  }
  return nextPreview.filter(
    widget =>
      widget.type === 'user_message' ||
      (widget.validated.facebook && !widget.validated.buttonDialogueInValid),
  );
};

export const useInteractivePreview = ({
  dialogueId,
  platforms,
  customAvailableWidgets,
  setWidgetCount,
}: {
  dialogueId: number;
  platforms?: PlatformType[];
  customAvailableWidgets?: WidgetTypes[];
  setWidgetCount?: (v: number) => void;
}) => {
  const { preview, widgetsByPageId: widgetsByPageIdGlobal } =
    useDialoguePreview({
      dialogueId,
      platforms,
      customAvailableWidgets,
      setWidgetCount,
    });
  const [previewToShow, setPreviewToShow] = useState<DialogueWidget[]>([]);
  const [previewToStore, setPreviewToStore] = useState<DialogueWidget[]>([]);
  const [nestedWidgets, setNestedWidgets] = useState<WidgetModel[]>([]);
  const [widgetByPageId, setWidgetByPageId] = useState<
    Map<number, WidgetModel[]>
  >(new Map());
  const [activeWidgetIndex, setActiveWidgetIndex] = useState<number>(0);

  const { t } = useTranslation();
  const prevDialogueId = usePrevious(dialogueId);

  const retrieveWidgets = async (pageId: number) => {
    return PageController.retrieveById(pageId);
  };

  const widgetHaveBtns = (w: WidgetModel | TextResponse) => {
    return (
      (w.buttons?.some(btn =>
        [ButtonType.PAGE, ButtonType.POSTBACK].includes(btn.type),
      ) &&
        w.type === WidgetTypes.QUICK_QUESTION) ||
      w.buttons?.some(
        btn =>
          ![
            ButtonType.PAGE,
            ButtonType.OPT_IN,
            ButtonType.EMAIL,
            ButtonType.CALL,
            ButtonType.URL,
            ButtonType.HANDOVER,
            ButtonType.PHONE_NUMBER,
          ].includes(btn.type),
      ) ||
      (w.type === WidgetTypes.FORM &&
        w.config.entries?.some(
          entry =>
            entry.buttons.length &&
            [ButtonType.POSTBACK].includes(entry.buttons?.[0]?.type),
        ))
    );
  };

  const getPreviousWidgets = async (
    nestedWidgetsLocal: WidgetModel[],
    widgetByPageIdProp?: Map<number, DialogueWidget[]>,
  ): Promise<DialogueWidget[]> => {
    let array: DialogueWidget[] = [];
    const widgetByPageIdLocal = widgetByPageIdProp ?? widgetByPageId;
    const recursiveGetWidgets = async (nestedWidgets: WidgetModel[]) => {
      if (nestedWidgets.length) {
        const lastItem = nestedWidgets[nestedWidgets.length - 1];
        const previousWidgets = widgetByPageIdLocal.get(lastItem.pageId);
        if (previousWidgets) {
          const lastItemIndex = previousWidgets.findIndex(
            w => w.id === lastItem.id,
          );
          const newNestedWidgets = nestedWidgets.slice(0, -1);

          setNestedWidgets(newNestedWidgets);
          const firstWidgetWithBtnIndex = previousWidgets.findIndex(
            (w, index) =>
              index > lastItemIndex &&
              w.id !== lastItem.id &&
              w.type !== 'user_message' &&
              widgetHaveBtns(w),
          );

          if (firstWidgetWithBtnIndex >= 0) {
            const partWidgets = previousWidgets.slice(
              lastItemIndex + 1,
              firstWidgetWithBtnIndex + 1,
            );
            const partWidgetsWithAnswers = await getWidgetsWithFormAndAnswers(
              partWidgets,
              retrieveWidgets,
              setWidgetByPageId,
              true,
              platforms,
            );

            array = [...array, ...partWidgetsWithAnswers];
          } else {
            const allWidgets = previousWidgets.slice(lastItemIndex + 1);
            const allWidgetsWithAnswers = await getWidgetsWithFormAndAnswers(
              allWidgets,
              retrieveWidgets,
              setWidgetByPageId,
              true,
              platforms,
            );

            array = [...array, ...allWidgetsWithAnswers];

            await recursiveGetWidgets(newNestedWidgets);
          }
        }
      }
    };
    await recursiveGetWidgets(nestedWidgetsLocal);
    return array;
  };

  const onSetPreviewToStore = async (
    widget: WidgetModel,
    btnType?: ButtonType,
  ) => {
    if (previewToStore.length) {
      const previewUpdated = previewToStore.map(w => {
        if (w.id === widget.id) {
          return widget;
        }
        return w;
      });
      const activeWidgetIndexLocal =
        widget.metadata?.previewIndex ?? activeWidgetIndex;
      let updatedNestedWidgets: WidgetModel[];
      if (btnType && [ButtonType.PAGE, ButtonType.OPT_IN].includes(btnType)) {
        updatedNestedWidgets = [];
      } else {
        const appearNestedWidgets = nestedWidgets.findIndex(
          nestedWidget => nestedWidget.pageId === widget.pageId,
        );
        if (appearNestedWidgets === -1) {
          updatedNestedWidgets = [...nestedWidgets, widget];
        } else {
          const copyNestedWidgets = [...nestedWidgets];
          copyNestedWidgets.splice(appearNestedWidgets);
          updatedNestedWidgets = [...copyNestedWidgets, widget];
        }
      }

      setNestedWidgets(updatedNestedWidgets);
      const nextDialogueItems = await getPreviewItemsForWidget(
        widget,
        retrieveWidgets,
        setWidgetByPageId,
      );

      const firstWidgetWithBtnIndex = nextDialogueItems.findIndex(
        w =>
          w.id !== widget.id &&
          w.type !== 'user_message' &&
          widgetHaveBtns(w as WidgetModel),
      );

      const copyPreview = [...previewUpdated].slice(0, activeWidgetIndexLocal);

      let nextDialogueItemsToShow: DialogueWidget[] = [];
      if (firstWidgetWithBtnIndex >= 0) {
        nextDialogueItemsToShow = await getWidgetsWithFormAndAnswers(
          nextDialogueItems.slice(0, firstWidgetWithBtnIndex + 1),
          retrieveWidgets,
          setWidgetByPageId,
          false,
          platforms,
        );
      } else {
        const prevWidgets = await getPreviousWidgets(updatedNestedWidgets);

        let itemsWithAnswers = await getWidgetsWithFormAndAnswers(
          nextDialogueItems,
          retrieveWidgets,
          setWidgetByPageId,
          false,
          platforms,
        );
        if (
          itemsWithAnswers.length === 1 &&
          itemsWithAnswers[0].type === WidgetTypes.NAVIGATE
        ) {
          const navigateWidget = itemsWithAnswers[0];
          itemsWithAnswers = [
            {
              ...navigateWidget,
              metadata: {
                ...navigateWidget.metadata,
                isPathEmpty: nextDialogueItems.length === 1,
                noSupportedWidgets: nextDialogueItems.length > 1,
              },
            },
          ];
        }
        const nextDialogueItemsToShow = [
          ...copyPreview,
          ...itemsWithAnswers,
          ...prevWidgets,
        ].map((w, idx) => {
          return {
            ...w,
            metadata: { ...(w as WidgetModel).metadata, previewIndex: idx },
          };
        });
        setPreviewToShow(nextDialogueItemsToShow);
        setActiveWidgetIndex(nextDialogueItemsToShow.length - 1);
        setPreviewToStore(nextDialogueItemsToShow);
        return;
      }

      const newPreviewToShow = [...copyPreview, ...nextDialogueItemsToShow].map(
        (w, idx) => {
          return {
            ...w,
            metadata: { ...(w as WidgetModel).metadata, previewIndex: idx },
          };
        },
      );
      setPreviewToShow(newPreviewToShow);
      setActiveWidgetIndex(activeWidgetIndexLocal);
      setPreviewToStore(newPreviewToShow);
    }
  };

  const getSelectedPageId = (
    widget: WidgetModel,
    selectedBtnIdx: number,
    selectedEntryIdx?: number,
  ): number | undefined => {
    if (widget.type === WidgetTypes.OTN) {
      return widget.config.pageId;
    }
    if (widget.type === WidgetTypes.GALLERY && selectedEntryIdx !== undefined) {
      const config = widget.config as GalleryWidgetConfig;
      const btn = config.entries[selectedEntryIdx].buttons[selectedBtnIdx] as {
        type: ButtonType;
        config: { pageId: number };
      };
      return btn.type === ButtonType.CALL || btn.type === ButtonType.URL
        ? undefined
        : btn.config.pageId;
    }
    const btn = (widget as WidgetModel).buttons[selectedBtnIdx] as {
      config: { pageId?: number };
    };
    return btn.config.pageId;
  };

  const mergePreviewList = async () => {
    let newWidgetsByPageId = new Map(cloneDeep(widgetByPageId));
    const widgetsByPageIdGlobalKeys = [...widgetsByPageIdGlobal.keys()];
    let newPreviewToStore = [...previewToStore];

    // tslint:disable-next-line:cyclomatic-complexity
    widgetsByPageIdGlobalKeys.forEach(pId => {
      const pageId = Number(pId);
      const pageWidgets = widgetByPageId.get(pageId);

      if (pageWidgets) {
        const updatedPageWidgets = cloneDeep(
          widgetsByPageIdGlobal.get(pageId) ?? [],
        );
        newWidgetsByPageId = new Map(newWidgetsByPageId).set(
          pageId,
          updatedPageWidgets,
        );

        let previewUpdated = false;
        if (updatedPageWidgets.length > pageWidgets.length) {
          const newWidget = differenceWith(
            updatedPageWidgets,
            pageWidgets,
            (x, y) => x.id === y.id,
          )[0] as WidgetModel;
          if (
            newWidget.validated.facebook &&
            !newWidget.validated.buttonDialogueInValid
          ) {
            previewUpdated = true;
            const newWidgetIndex = updatedPageWidgets.findIndex(
              w => w.id === newWidget.id,
            );
            const firstWidgetInPath = previewToStore.findIndex(
              w =>
                !isUserMessage(w) &&
                (w as WidgetModel).pageId === newWidget.pageId,
            );
            const firstWidgetForPathIndex =
              firstWidgetInPath < 0
                ? isUserMessage(previewToStore[previewToStore.length - 1])
                  ? previewToStore.length - 1
                  : previewToStore.length
                : firstWidgetInPath;

            if (newWidgetIndex === 0) {
              const prevItems =
                firstWidgetForPathIndex > 0
                  ? [...previewToStore].slice(0, firstWidgetForPathIndex)
                  : [];
              const nextItems = [...previewToStore].slice(
                firstWidgetForPathIndex,
              );

              const isWidgetHaveBtns = widgetHaveBtns(newWidget);
              const lastPrevItems = prevItems[prevItems.length - 1];
              if (lastPrevItems?.type === WidgetTypes.NAVIGATE) {
                prevItems.splice(prevItems.length - 1, 1, {
                  ...lastPrevItems,
                  metadata: {
                    ...lastPrevItems.metadata,
                    isPathEmpty: false,
                    noSupportedWidgets: false,
                  },
                });
              }
              if (isWidgetHaveBtns) {
                const widgetBtns = newWidget.buttons as {
                  config: { pageId: number };
                }[];

                const btnsWithoutPathIndex = widgetBtns.findIndex(
                  btn => !btn.config.pageId,
                );

                newPreviewToStore = [
                  ...prevItems,
                  {
                    ...newWidget,
                    metadata: {
                      ...newWidget.metadata,
                      selectedBtnIdx:
                        btnsWithoutPathIndex !== -1
                          ? btnsWithoutPathIndex
                          : undefined,
                    },
                  },
                  ...nextItems,
                ];
              } else {
                newPreviewToStore = [...prevItems, newWidget, ...nextItems];
              }
            } else if (newWidgetIndex === updatedPageWidgets.length - 1) {
              const previewToStoreLastWidget = findLast(
                previewToStore,
                w => !isUserMessage(w),
              ) as WidgetModel | undefined;

              if (
                (newWidget.pageId === previewToStoreLastWidget?.pageId &&
                  previewToStoreLastWidget?.id ===
                    pageWidgets[pageWidgets.length - 1].id) ||
                !previewToStoreLastWidget
              ) {
                if (
                  previewToStoreLastWidget &&
                  widgetHaveBtns(previewToStoreLastWidget) &&
                  previewToStoreLastWidget.metadata?.selectedBtnIdx ===
                    undefined
                ) {
                  newPreviewToStore = [...newPreviewToStore];
                } else {
                  newPreviewToStore = [...newPreviewToStore, newWidget];
                }
              }
            } else {
              const widgetBeforeNew = updatedPageWidgets[newWidgetIndex - 1];
              const widgetBeforeNewIndexInPreview = newPreviewToStore.findIndex(
                w => w.id === widgetBeforeNew.id,
              );
              const newWidgetIndexInPreview = firstWidgetForPathIndex
                ? newWidgetIndex
                : newPreviewToStore[widgetBeforeNewIndexInPreview + 1] &&
                  isUserMessage(
                    newPreviewToStore[widgetBeforeNewIndexInPreview + 1],
                  )
                ? widgetBeforeNewIndexInPreview + 2
                : widgetBeforeNewIndexInPreview + 1;

              const prevItems = [...newPreviewToStore].slice(
                0,
                firstWidgetForPathIndex + newWidgetIndexInPreview,
              );
              const nextItems = [...newPreviewToStore].slice(
                firstWidgetForPathIndex + newWidgetIndexInPreview,
              );
              const lastPrevItems = findLast(
                prevItems,
                w => !isUserMessage(w),
              ) as WidgetModel | undefined;

              const firstNextItems = nextItems.find(w => !isUserMessage(w)) as
                | WidgetModel
                | undefined;

              if (
                lastPrevItems &&
                firstNextItems?.pageId === lastPrevItems.pageId
              ) {
                const isDisplayedInPreview = prevItems.some(
                  item => item.id === lastPrevItems.id,
                );
                const shouldShowNewWidget = widgetHaveBtns(lastPrevItems)
                  ? (lastPrevItems.metadata?.selectedBtnIdx !== undefined ||
                      lastPrevItems.metadata?.selectedBtnForEntry?.some(
                        idx => idx !== undefined,
                      )) &&
                    isDisplayedInPreview
                  : isDisplayedInPreview;
                if (shouldShowNewWidget) {
                  const widgetBtns = newWidget.buttons as {
                    config: { pageId: number };
                  }[];
                  const btnsWithoutPathIndex = widgetBtns.findIndex(
                    btn => !btn.config.pageId,
                  );

                  newPreviewToStore = [
                    ...prevItems,
                    {
                      ...newWidget,
                      metadata: {
                        ...newWidget.metadata,
                        selectedBtnIdx:
                          btnsWithoutPathIndex !== -1
                            ? btnsWithoutPathIndex
                            : undefined,
                      },
                    },
                    ...nextItems,
                  ];
                }
              }
            }
          }
        }
        if (updatedPageWidgets.length === pageWidgets.length) {
          const widgetsChanged = updatedPageWidgets.filter(
            (pageWidget, index) => pageWidget.id !== pageWidgets[index].id,
          );
          const widgetsChangedLength = widgetsChanged.length;

          if (widgetsChangedLength) {
            previewUpdated = true;
            let dragItem: WidgetModel | undefined;
            let dragIndex = 0;
            let widgetToPaste: WidgetModel | undefined;
            const updatedWidgetsPageId = updatedPageWidgets[0].pageId;
            if (
              (previewToStore[0] as WidgetModel).pageId !== updatedWidgetsPageId
            ) {
              const pastePathIndex = previewToStore.findIndex(
                w =>
                  !isUserMessage(w) &&
                  (w as WidgetModel).pageId === updatedWidgetsPageId,
              );
              const previewWithoutPathWidgets = previewToStore.filter(
                w =>
                  isUserMessage(w) ||
                  (!isUserMessage(w) &&
                    (w as WidgetModel).pageId ===
                      (previewToStore[0] as WidgetModel).pageId),
              );
              const firstWidgetWithBtnIndex =
                updatedPageWidgets.findIndex(widgetHaveBtns);
              const newPathWidgets =
                firstWidgetWithBtnIndex === -1
                  ? updatedPageWidgets
                  : firstWidgetWithBtnIndex === 0
                  ? [updatedPageWidgets[0]]
                  : [...updatedPageWidgets].slice(0, firstWidgetWithBtnIndex);
              const previewUpdated = [...previewWithoutPathWidgets];
              previewUpdated.splice(pastePathIndex, 0, ...newPathWidgets);
              newPreviewToStore = previewUpdated;
              return;
            }
            const previewWithoutUserMessages = [...previewToStore].filter(
              w => w.type !== 'user_message',
            );

            previewWithoutUserMessages.forEach((pageWidget, index) => {
              if (!dragItem) {
                if (pageWidget.id === updatedPageWidgets[index]?.id) {
                  return;
                }
                if (pageWidget.id === updatedPageWidgets[index + 1]?.id) {
                  dragItem = updatedPageWidgets[index];
                  widgetToPaste = pageWidget as WidgetModel;
                  return;
                }
                if (pageWidget.id === updatedPageWidgets[index - 1]?.id) {
                  dragItem = previewWithoutUserMessages[
                    index - 1
                  ] as WidgetModel;
                }
              }
            });

            dragItem = previewToStore.find(w => w.id === dragItem?.id) as
              | WidgetModel
              | undefined;

            dragIndex = widgetToPaste
              ? previewToStore.findIndex(w => w.id === widgetToPaste?.id)
              : previewToStore.findIndex(
                  w => w.id === widgetsChanged[widgetsChangedLength - 2].id,
                ) + 1;
            dragIndex =
              previewToStore[dragIndex]?.type === 'user_message'
                ? dragIndex + 1
                : dragIndex;

            if (!dragItem) {
              const firstWidgetWithBtnIndex =
                updatedPageWidgets.findIndex(widgetHaveBtns);
              newPreviewToStore =
                firstWidgetWithBtnIndex === -1
                  ? updatedPageWidgets
                  : [...updatedPageWidgets].slice(0, firstWidgetWithBtnIndex);
              return;
            }

            if (dragItem) {
              let skip = false;

              const previewNew = previewToStore.reduce(
                (acc: DialogueWidget[], widget, index) => {
                  if (skip) {
                    return acc;
                  }
                  if (isUserMessage(widget)) {
                    return [...acc, widget];
                  }
                  if (widget.id === dragItem?.id) {
                    return acc;
                  }
                  if (index === dragIndex && dragItem) {
                    const selectedBtnIdx = dragItem.metadata?.selectedBtnIdx;
                    const selectedEntryIdx =
                      dragItem.metadata?.selectedEntryIdx;
                    if (widgetHaveBtns(dragItem)) {
                      skip = true;
                      if (dragItem.type === WidgetTypes.NAVIGATE) {
                        dragItem = {
                          ...dragItem,
                          metadata: {
                            ...dragItem.metadata,
                            selectedBtnIdx: undefined,
                          },
                        };
                      }
                      return [...acc, dragItem];
                    }
                    if (selectedBtnIdx !== undefined) {
                      const pageIdToCopy = getSelectedPageId(
                        dragItem,
                        selectedBtnIdx,
                        selectedEntryIdx,
                      );
                      if (pageIdToCopy) {
                        const copyWidgets = previewToStore.filter(
                          (w, idx) =>
                            (!isUserMessage(w) &&
                              (w as WidgetModel).pageId === pageIdToCopy) ||
                            (isUserMessage(w) &&
                              (previewToStore[idx - 1] as WidgetModel)
                                .pageId === pageIdToCopy),
                        );
                        skip = true;
                        return [...acc, dragItem, ...copyWidgets];
                      }
                      return [...acc, dragItem, widget];
                    }
                    return [...acc, dragItem, widget];
                  }
                  return [...acc, widget];
                },
                [],
              );
              newPreviewToStore = previewNew;
            }
          }
        }

        if (!previewUpdated) {
          let removeAnswer = false;
          let pageIdToDelete: number | undefined;
          newPreviewToStore = newPreviewToStore.reduce(
            (acc: DialogueWidget[], w, index) => {
              if (isUserMessage(w)) {
                if (removeAnswer) {
                  removeAnswer = false;
                  return acc;
                }
                return [...acc, w];
              }
              if ((w as WidgetModel).pageId === pageIdToDelete) {
                return acc;
              }
              pageIdToDelete = undefined;
              if ((w as WidgetModel).pageId === pageId) {
                const widgetExists = updatedPageWidgets.find(
                  pageW => pageW.id === w.id,
                );
                const selectedBtnIdx = (w as WidgetModel).metadata
                  ?.selectedBtnIdx;
                const selectedEntryIdx = (w as WidgetModel).metadata
                  ?.selectedEntryIdx;

                if (widgetExists) {
                  if (selectedBtnIdx !== undefined) {
                    let btn;
                    let newBtn;
                    if (w.type === WidgetTypes.OTN) {
                      btn = {
                        config: { pageId: w.config.pageId },
                      };
                      newBtn = {
                        config: { pageId: widgetExists.config.pageId },
                      };
                    } else if (w.type === WidgetTypes.NAVIGATE) {
                      btn = {
                        config: { pageId: w.config.pageId },
                      };
                      newBtn = {
                        config: { pageId: widgetExists.config.pageId },
                      };
                    } else if (
                      w.type === WidgetTypes.GALLERY &&
                      selectedEntryIdx !== undefined
                    ) {
                      const config = w.config as GalleryWidgetConfig;
                      btn =
                        config.entries[selectedEntryIdx].buttons[
                          selectedBtnIdx
                        ];
                      newBtn = widgetExists.config.entries?.[selectedEntryIdx]
                        .buttons[selectedBtnIdx] as
                        | undefined
                        | {
                            config: { pageId: number };
                          };
                      newBtn =
                        newBtn?.type === ButtonType.CALL ||
                        newBtn?.type === ButtonType.URL
                          ? undefined
                          : newBtn;
                    } else {
                      btn = (w as WidgetModel).buttons[selectedBtnIdx] as {
                        config: { pageId: number };
                      };
                      newBtn = (widgetExists as WidgetModel).buttons[
                        selectedBtnIdx
                      ] as
                        | undefined
                        | {
                            config: { pageId: number };
                          };
                    }

                    if (
                      !btn ||
                      !newBtn ||
                      btn.config?.pageId !== newBtn.config?.pageId
                    ) {
                      pageIdToDelete = btn.config.pageId;
                      return [...acc, widgetExists];
                    }
                  }

                  return [
                    ...acc,
                    {
                      ...widgetExists,
                      metadata: (w as WidgetModel).metadata,
                    },
                  ];
                }
                if (
                  newPreviewToStore[index + 1] &&
                  isUserMessage(newPreviewToStore[index + 1])
                ) {
                  removeAnswer = true;
                }
                if (selectedBtnIdx !== undefined) {
                  pageIdToDelete = getSelectedPageId(
                    w as WidgetModel,
                    selectedBtnIdx,
                    selectedEntryIdx,
                  );
                }
                if (
                  newPreviewToStore[index - 1]?.type === WidgetTypes.NAVIGATE
                ) {
                  const nextPathWidget = newPreviewToStore[index + 1]
                    ? isUserMessage(newPreviewToStore[index + 1])
                      ? newPreviewToStore[index + 2]
                      : newPreviewToStore[index + 1]
                    : undefined;

                  if (!nextPathWidget) {
                    const navigateWidget = newPreviewToStore[
                      index - 1
                    ] as WidgetModel;
                    const navigateWidgetEmptyPath = {
                      ...navigateWidget,
                      metadata: {
                        ...navigateWidget.metadata,
                        isPathEmpty: true,
                      },
                    };
                    const newArray = [...acc];
                    newArray.splice(acc.length - 1, 1, navigateWidgetEmptyPath);
                    return newArray;
                  }
                }
                return acc;
              }
              return [...acc, w];
            },
            [],
          );
        }
      }
    });
    setWidgetByPageId(newWidgetsByPageId);
    const withoutUserMessages = newPreviewToStore.filter(
      w =>
        !isUserMessage(w) &&
        (w as WidgetModel).validated.facebook &&
        !(w as WidgetModel).validated.buttonDialogueInValid,
    ) as WidgetModel[];
    const newPreviewToStoreWithAnswers = await withoutUserMessages.reduce(
      async (acc, w) => {
        const elements = await acc;
        if (w.type === WidgetTypes.FORM) {
          const el = await getPreviewItemsForWidget(
            w,
            retrieveWidgets,
            setWidgetByPageId,
          );
          return [...elements, ...el];
        }
        if (w.type === WidgetTypes.NAVIGATE) {
          return [...elements, w];
        }
        if (w.type === WidgetTypes.OTN) {
          const restWidgets =
            w.metadata?.selectedBtnIdx !== undefined
              ? [getUserMessage('Notify me')]
              : [];

          return [...elements, w, ...restWidgets];
        }
        if (w.type === WidgetTypes.GALLERY) {
          const selectedEntryIdx = w.metadata?.selectedEntryIdx;
          const selectedBtnIdx = w.metadata?.selectedBtnIdx;
          if (
            selectedEntryIdx !== undefined &&
            selectedBtnIdx !== undefined &&
            w.config.entries
          ) {
            const btn =
              w.config.entries[selectedEntryIdx].buttons[selectedBtnIdx];
            return [...elements, w, getUserMessage(btn?.name ?? 'btn')];
          }
          return [...elements, w];
        }
        if (
          [ButtonType.EMAIL, ButtonType.PHONE_NUMBER].includes(
            w.buttons?.[0]?.type,
          )
        ) {
          return [...elements, w, ...getFreeTextResponse(w.buttons?.[0].type)];
        }
        if (
          !w.buttons.length &&
          (w.config as WidgetConfigEvenLessUseful).allowFreeTextResponse
        ) {
          return [...elements, w, ...getFreeTextResponse('free')];
        }
        const btnIndex = (w as WidgetModel).metadata?.selectedBtnIdx;
        if (btnIndex !== undefined) {
          const btn = (w as WidgetModel).buttons[btnIndex];
          return [...elements, w, getUserMessage(btn?.name ?? 'btn')];
        }
        return [...elements, w];
      },
      Promise.resolve([] as DialogueWidget[]),
    );
    const lastItemInPreview = withoutUserMessages[
      withoutUserMessages.length - 1
    ] as WidgetModel | undefined;
    if (lastItemInPreview) {
      const widgetsForLastItem = newWidgetsByPageId.get(
        lastItemInPreview.pageId,
      );

      if (
        (widgetsForLastItem &&
          lastItemInPreview.id ===
            widgetsForLastItem[widgetsForLastItem.length - 1].id) ||
        (widgetHaveBtns(lastItemInPreview) &&
          lastItemInPreview.metadata?.selectedBtnIdx === undefined)
      ) {
        return newPreviewToStoreWithAnswers;
      }

      const previousWidgets = await getPreviousWidgets(
        [lastItemInPreview],
        newWidgetsByPageId,
      );
      const lastNewWidget = findLast(
        newPreviewToStoreWithAnswers,
        w => !isUserMessage(w),
      ) as WidgetModel;
      const selectedBtnIdx = lastNewWidget.metadata?.selectedBtnIdx;
      const selectedEntryIdx = lastNewWidget.metadata?.selectedEntryIdx;
      if (
        !isUserMessage(previousWidgets[0]) &&
        (previousWidgets[0] as WidgetModel)?.pageId === lastNewWidget.pageId
      ) {
        if (selectedBtnIdx !== undefined) {
          if (
            (lastNewWidget.type === WidgetTypes.GALLERY &&
              selectedEntryIdx !== undefined &&
              lastNewWidget.config.entries?.[selectedEntryIdx].buttons[
                selectedBtnIdx
              ].type === ButtonType.PAGE) ||
            lastNewWidget.type === WidgetTypes.OTN ||
            lastNewWidget.type === WidgetTypes.NAVIGATE ||
            lastNewWidget.buttons[selectedBtnIdx].type === ButtonType.PAGE
          ) {
            return [...newPreviewToStoreWithAnswers];
          }
        }
        return [...newPreviewToStoreWithAnswers, ...previousWidgets];
      }
      return [...newPreviewToStoreWithAnswers];
    }
    return newPreviewToStoreWithAnswers;
  };

  useEffect(() => {
    const getPreview = async () => {
      if (!preview.length) {
        setPreviewToShow([]);
        setPreviewToStore([]);
      } else if (!previewToShow.length || prevDialogueId !== dialogueId) {
        setWidgetByPageId(
          new Map(widgetByPageId).set(preview[0].pageId, preview),
        );
        setNestedWidgets([]);

        const firstWidgetWithBtnIndex = preview.findIndex(widgetHaveBtns);

        if (firstWidgetWithBtnIndex >= 0) {
          const nextDialogueItems = await getWidgetsWithFormAndAnswers(
            [...preview.slice(0, firstWidgetWithBtnIndex + 1)],
            retrieveWidgets,
            setWidgetByPageId,
            true,
            platforms,
          );
          const newPreview = nextDialogueItems.map((w, idx) => {
            return {
              ...w,
              metadata: { ...(w as WidgetModel).metadata, previewIndex: idx },
            };
          });
          setPreviewToStore(newPreview);
          setPreviewToShow(newPreview);
        } else {
          const previewWithAnswers = await getWidgetsWithFormAndAnswers(
            preview,
            retrieveWidgets,
            setWidgetByPageId,
            true,
            platforms,
          );
          const newPreview = previewWithAnswers.map((w, idx) => {
            return {
              ...w,
              metadata: { ...(w as WidgetModel).metadata, previewIndex: idx },
            };
          });
          setPreviewToStore(newPreview);
          setPreviewToShow(newPreview);
        }
      } else {
        const mergedWidgets = await mergePreviewList();
        const mergedWidgetsWithIndex = mergedWidgets.map((w, idx) => {
          return {
            ...w,
            metadata: { ...(w as WidgetModel).metadata, previewIndex: idx },
          };
        });

        setPreviewToStore(mergedWidgetsWithIndex);
        setPreviewToShow(mergedWidgetsWithIndex);
      }
    };

    setTimeout(async () => {
      await getPreview();
    }, 100);
  }, [JSON.stringify(preview), JSON.stringify(widgetsByPageIdGlobal)]);

  return {
    previewList: previewToShow,
    activeWidgetIndex,
    setActiveWidgetIndex,
    setPreviewToStore: onSetPreviewToStore,
  };
};
